import {
  IReportEvents,
  TStiReportData,
} from '@infotech-soe/merps/types/Reports';
import { ActionName } from '@infotech-soe/metaschema';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { TActionArgsValues } from '../../infrastructure/providers/abstract/IApiProvider';
import CommandPrescript from '../../services/commands/CommandPrescript';
import {
  UserInteractor,
  UserInteractorType,
} from '../../services/commands/UserInteractor';
import UniqueId from '../../services/uid/UniqueId';

import { failureAction, pendingAction, successAction } from './actionHandler';

export interface ReviewState {
  data?: TStiReportData['data'];
  isOpen: boolean;
  template?: string;
  variables?: Record<string, any>;
  events?: IReportEvents;
  parseDates?: boolean;
  nextAction?: ActionName;
  datasetId?: UniqueId;
}

const initialState: ReviewState = {
  isOpen: false,
};

type GetDataSuccessAction =
  | {
      command: string;
      datasetId: UniqueId;
      type: UserInteractorType.REVIEW_FORM;
      interactor: Extract<
        UserInteractor,
        { type: UserInteractorType.REVIEW_FORM }
      >;
      data: TStiReportData['data'];
      template: string;
      variables?: Record<string, any>;
      parseDates?: boolean;
      width: number;
    }
  | {
      type: UserInteractorType.PRINT_FORM_ON_CLIENT;
      interactor: Extract<
        UserInteractor,
        { type: UserInteractorType.PRINT_FORM_ON_CLIENT }
      >;
      data: TStiReportData['data'];
      template: string;
      variables?: Record<string, any>;
      events?: IReportEvents;
      parseDates?: boolean;
    };

const reviewSlice = createSlice({
  name: 'review',
  initialState,
  reducers: {
    getData: pendingAction<
      ReviewState,
      {
        actionName: string;
        datasetId: UniqueId;
        prescript: CommandPrescript<TActionArgsValues>;
      }
    >(),
    getDataSuccess: successAction<ReviewState, GetDataSuccessAction>(),
    getDataFailure: failureAction(),
    open(
      state,
      {
        payload: {
          data,
          template,
          variables,
          events,
          parseDates,
          nextAction,
          datasetId,
        },
      }: PayloadAction<{
        data: Record<string, any>;
        template: string;
        variables?: Record<string, any>;
        events?: IReportEvents;
        parseDates?: boolean;
        nextAction?: ActionName;
        datasetId?: UniqueId;
      }>
    ) {
      state.isOpen = true;
      state.template = template;
      state.data = data;
      state.variables = variables;
      state.events = events;
      state.parseDates = parseDates;
      state.nextAction = nextAction;
      state.datasetId = datasetId;
    },
    close: pendingAction(),
    closeSuccess: successAction(() => {
      return initialState;
    }),
    closeFailure: failureAction(),
    finishReview(state) {
      state.nextAction = undefined;
    },
  },
});

export const reviewReducer = reviewSlice.reducer;

export default reviewSlice.actions;
